/* Molten silver: raw WebGL fragment shader (flow noise + fresnel highlight). No 3D engine.
   Starts after the LCP image has painted, pauses off-screen / hidden tab, off under reduced motion.
   Mobile: half resolution, fewer octaves, autonomous drift instead of cursor. */
(() => {
  const FRAG = `
#extension GL_OES_standard_derivatives : enable
precision mediump float;
uniform vec2 uRes; uniform float uTime; uniform vec2 uMouse; uniform float uPush; uniform int uOct;
float hash(vec2 p){ p = fract(p*vec2(123.34, 456.21)); p += dot(p, p+45.32); return fract(p.x*p.y); }
float noise(vec2 p){ vec2 i=floor(p), f=fract(p); vec2 u=f*f*(3.-2.*f);
  return mix(mix(hash(i),hash(i+vec2(1,0)),u.x), mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),u.x), u.y); }
float fbm(vec2 p){ float v=0., a=.5; for(int i=0;i<5;i++){ if(i>=uOct) break; v+=a*noise(p); p=p*2.03+vec2(1.7,9.2); a*=.5; } return v; }
void main(){
  vec2 uv = gl_FragCoord.xy / uRes; float ar = uRes.x/uRes.y;
  vec2 p = uv * vec2(ar, 1.) * 3.; vec2 m = uMouse * vec2(ar, 1.) * 3.;   // denser satin ripples
  float d = distance(p, m);
  p += normalize(p - m + .0001) * uPush * .25 * exp(-d*d*2.2);            // cursor pushes the metal
  vec2 q = vec2(fbm(p + uTime*.07), fbm(p + vec2(5.2,1.3) - uTime*.06));   // one domain warp: viscous flow
  float h = fbm(p + 2.2*q);
  vec3 n = normalize(vec3(-dFdx(h)*uRes.x*.03, -dFdy(h)*uRes.y*.03, 1.));  // normals from derivatives, no extra samples; strong relief
  vec3 light = normalize(vec3((uMouse - .5) * .6 + vec2(-.45, .35), .6));  // raking base light so the relief reads without a cursor
  float diff = clamp(dot(n, light), 0., 1.);
  float fres = pow(1. - n.z, 2.);
  float spec = pow(clamp(dot(reflect(-light, n), vec3(0,0,1)), 0., 1.), 24.);
  vec3 silver = mix(vec3(.76,.77,.79), vec3(.97,.97,.975), diff*.6 + h*.5);        // deeper silver range so the flow reads
  silver = mix(silver, vec3(.84,.76,.83), fres*.75);                               // lilac in the folds
  silver += spec*.7 + exp(-d*d*6.) * uPush * .18;                                  // travelling highlight
  gl_FragColor = vec4(silver, 1.);
}`;
  const VERT = 'attribute vec2 a; void main(){ gl_Position = vec4(a, 0., 1.); }';

  function start(canvas) {
    const tier = window.NS?.tier || {};
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power', preserveDrawingBuffer: false, failIfMajorPerformanceCaveat: true });
    if (!gl || !gl.getExtension('OES_standard_derivatives')) return null;
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    if (dbg && /swiftshader|llvmpipe|software|basic render/i.test(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))) return null;
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null; };
    const vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;
    const prog = gl.createProgram(); gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = (n) => gl.getUniformLocation(prog, n);
    const uRes = U('uRes'), uTime = U('uTime'), uMouse = U('uMouse'), uPush = U('uPush'), uOct = U('uOct');
    gl.uniform1i(uOct, tier.low ? 3 : 5);

    // Render below CSS size: 0.35x (mobile) is cheap; desktop at 0.75x keeps the fine ripples crisp.
    const scale = (tier.low ? 0.35 : 0.75) * Math.min(tier.dpr || 1, 1.5);
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(r.width * scale)); canvas.height = Math.max(1, Math.round(r.height * scale));
      gl.viewport(0, 0, canvas.width, canvas.height); gl.uniform2f(uRes, canvas.width, canvas.height);
    };
    const ro = new ResizeObserver(resize); ro.observe(canvas); resize();

    const target = { x: 0.62, y: 0.55 }, cur = { x: 0.62, y: 0.55 };
    let push = 0, pushTarget = 0, raf = 0, elapsed = 0, last = 0, hovering = false;
    const host = canvas.closest('[data-hero]');
    const onMove = (e) => {
      const r = host.getBoundingClientRect();
      target.x = (e.clientX - r.left) / r.width; target.y = 1 - (e.clientY - r.top) / r.height; pushTarget = 1; hovering = true;
    };
    const onLeave = () => { hovering = false; };
    if (tier.fine) { host.addEventListener('pointermove', onMove, { passive: true }); host.addEventListener('pointerleave', onLeave); }

    let slow = 0, api, dead = false;
    const born = performance.now();
    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = now - last;
      if (dt < (tier.low ? 33 : 16)) return;   // ~30fps cap on low-power devices
      // watchdog: only a sustained run of slow frames (not page-load jank) hands back to the CSS silver fallback
      if (now - born > 2500) { slow = dt > 48 ? slow + 1 : 0; if (slow > 45) { api.destroy(true); return; } }
      elapsed += Math.min(dt, 50) / 1000; last = now;
      // no cursor over the hero (initial load, touch, pointer left): the highlight drifts on its own
      if (!hovering) { target.x = 0.5 + Math.sin(elapsed * 0.13) * 0.3; target.y = 0.5 + Math.cos(elapsed * 0.09) * 0.25; pushTarget = 0.8; }
      cur.x += (target.x - cur.x) * 0.06; cur.y += (target.y - cur.y) * 0.06; push += (pushTarget - push) * 0.04;
      gl.uniform1f(uTime, elapsed); gl.uniform2f(uMouse, cur.x, cur.y); gl.uniform1f(uPush, push);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    return (api = {
      play() { if (!raf && !dead) { last = performance.now(); raf = requestAnimationFrame(frame); canvas.classList.add('is-on'); } },
      pause() { cancelAnimationFrame(raf); raf = 0; },
      destroy(giveUp) {
        if (dead) return; dead = true;
        cancelAnimationFrame(raf); raf = 0; ro.disconnect();
        host.removeEventListener('pointermove', onMove); host.removeEventListener('pointerleave', onLeave);
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        canvas.classList.remove('is-on'); canvas.parentElement.classList.remove('has-gl');
        if (giveUp) { canvas.remove(); return; }
        // a fresh canvas so re-creation gets a new context
        const fresh = canvas.cloneNode(); canvas.replaceWith(fresh);
        queueMicrotask(() => init(fresh));
      },
    });
  }

  function init(canvas) {
    if (!window.NS?.motion || NS.tier.reduced) return;
    const hero = canvas.closest('[data-hero]');
    const lcp = hero.querySelector('img[fetchpriority="high"]');
    let booted = false;
    const boot = () => { if (booted) return; booted = true; setTimeout(() => {
      NS.motion.register(canvas, () => {
        const api = start(canvas);
        if (api) canvas.parentElement.classList.add('has-gl');
        return api || {};
      });
    }, 120); };
    // start after the LCP image paints, but never wait longer than 1.5s for it
    if (!lcp || lcp.complete) boot(); else { lcp.addEventListener('load', boot, { once: true }); setTimeout(boot, 1500); }
  }

  const run = () => document.querySelectorAll('[data-hero-silver]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
})();

/* Hero pointer depth: 3D tilt on the image stack + aura drift toward the cursor. Fine pointers only. */
(() => {
  const run = () => document.querySelectorAll('[data-hero]').forEach((hero) => {
    if (hero.dataset.tilt || !window.NS?.tier?.fine || NS.tier.reduced || hero.dataset.motionLevel === 'none') return;
    hero.dataset.tilt = 1;
    let raf = 0;
    hero.addEventListener('pointermove', (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = hero.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        hero.style.setProperty('--px', px.toFixed(3)); hero.style.setProperty('--py', py.toFixed(3));
        hero.querySelectorAll('[data-hero-tilt]').forEach((m) => { m.style.setProperty('--rx', (px * 12).toFixed(2) + 'deg'); m.style.setProperty('--ry', (-py * 10).toFixed(2) + 'deg'); });
      });
    }, { passive: true });
    hero.addEventListener('pointerleave', () => {
      cancelAnimationFrame(raf);
      ['--px', '--py'].forEach((v) => hero.style.removeProperty(v));
      hero.querySelectorAll('[data-hero-tilt]').forEach((m) => { m.style.removeProperty('--rx'); m.style.removeProperty('--ry'); });
    });
  });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  document.addEventListener('shopify:section:load', run);
})();
